import { Deployment } from '@liveauctioneers/hammer-ui-payments/types';

import type { ConfigSlice } from '@/redux/modules/config';

/** Path on analytics-service for app store redirect (see LAAC-1714). */
export const APP_DOWNLOAD_REDIRECT_PATH = '/app/download';

export const AppDownloadSource = {
    LeadingBidder: 'Leading Bidder',
    /** Post–saved item / watch moment (LAAC-1674). */
    SavedItem: 'Saved Item',
};

export type AppDownloadSourceValue = (typeof AppDownloadSource)[keyof typeof AppDownloadSource];

export const AppDownloadTrigger = {
    MobileCtaClick: 'Mobile CTA Click',
    QrScan: 'QR Scan',
};

export type AppDownloadTriggerValue = (typeof AppDownloadTrigger)[keyof typeof AppDownloadTrigger];

type BuildAppDownloadRedirectUrlArgs = {
    bidderId: number;
    deployment: ConfigSlice['deployment'];
    source: AppDownloadSourceValue;
    trigger: AppDownloadTriggerValue;
};

/**
 * Base origin for analytics-service app-download redirects.
 * https://analytics-service-{deployment}.liveauctioneers.com
 */
export const getAnalyticsServiceAppDownloadOrigin = (deployment: ConfigSlice['deployment']): string => {
    let safeDeployment = deployment;
    if (!deployment || deployment === Deployment.Prod || deployment === Deployment.PreProd || deployment === 'test') {
        safeDeployment = Deployment.Prod;
    }
    return `https://analytics-service-${safeDeployment}.liveauctioneers.com`;
};

/**
 * Full URL for the app-download redirect endpoint with attribution query params.
 * Omits `bidder_id` when not available (<= 0), matching server expectations for anonymous users.
 */
export const buildAppDownloadRedirectUrl = ({
    bidderId,
    deployment,
    source,
    trigger,
}: BuildAppDownloadRedirectUrlArgs): string => {
    const origin = getAnalyticsServiceAppDownloadOrigin(deployment);
    const url = new URL(APP_DOWNLOAD_REDIRECT_PATH, origin);

    if (bidderId > 0) {
        url.searchParams.set('bidder_id', String(bidderId));
    }
    url.searchParams.set('source', source);
    url.searchParams.set('trigger', trigger);

    return url.toString();
};
